<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useAttendanceStore } from '../stores/attendance';
import { useRouter } from 'vue-router';
import { Clock, HandCoins, ChevronRight, FileText, Receipt, TrendingUp, Wallet, Trophy, MapPin, FileCheck, Calendar, BookOpen, Bell, Banknote, Users } from 'lucide-vue-next';
import { useVolunteerStore } from '../stores/volunteer';
import { useAnnouncementStore } from '../stores/announcement';
import Swal from 'sweetalert2';

const authStore = useAuthStore();
const attendanceStore = useAttendanceStore();
const volunteerStore = useVolunteerStore();
const announcementStore = useAnnouncementStore();
const router = useRouter();

const permissionState = ref(Notification.permission);

const requestNotification = async () => {
    try {
        await authStore.initFcm();
        permissionState.value = Notification.permission;
    } catch (e: any) {
        Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: "Gagal mengaktifkan notifikasi: " + e?.message,
        });
    }
};

onMounted(async () => {
  if (!authStore.user) await authStore.fetchUser();
  attendanceStore.checkStatus();
  volunteerStore.fetchDashboard();
  announcementStore.fetchAnnouncements();
  permissionState.value = Notification.permission;
});

const navigate = (path: string) => router.push(path);
</script>

<template>
  <div class="p-4 pb-28">
    <!-- Header -->
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center gap-3" @click="navigate('/profile')">
          <!-- Profile Pic -->
          <div class="w-12 h-12 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center text-primary font-bold shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
            <img 
                v-if="authStore.user?.photo_url" 
                :src="authStore.user.photo_url" 
                alt="Foto Profil" 
                class="w-full h-full object-cover"
              >
            <span v-else>{{ authStore.user?.name?.charAt(0) }}</span>
          </div>
          <div>
            <h1 class="text-base font-bold text-slate-900 dark:text-white leading-tight">{{ authStore.user?.name || 'Karyawan' }}</h1>
            <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">{{ authStore.user?.position?.name || 'Karyawan' }}</p>
          </div>
      </div>
      
      <!-- Notification Button -->
      <button @click="navigate('/inbox')" class="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-600 dark:text-slate-300 shadow-sm border border-slate-100 dark:border-slate-700 relative active:scale-95 transition-transform">
          <Bell class="w-5 h-5" />
          <span v-if="announcementStore.announcements.length > 0" class="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full ring-1 ring-white dark:ring-slate-800"></span>
      </button>
    </div>

    <!-- Notification Permission Warning -->
    <div v-if="permissionState === 'default' || permissionState === 'denied'" class="mb-6">
       <button 
         @click="requestNotification"
         class="w-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-3 text-left active:bg-blue-100 dark:active:bg-blue-900/30"
       >
         <div class="bg-blue-500 rounded-full p-2 text-white">
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bell"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
         </div>
         <div class="flex-1">
            <h3 class="font-bold text-blue-900 dark:text-blue-100 text-sm">Aktifkan Notifikasi</h3>
            <p class="text-blue-700 dark:text-blue-300 text-xs">Ketuk disini agar Anda menerima notifikasi bisyaroh & pengumuman.</p>
         </div>
       </button>
    </div>

    <div v-if="false" class="mb-6">
       <!-- Volunteer Summary Card -->
       <div class="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white shadow-lg mb-4">
          <div class="flex justify-between items-start mb-4">
             <div>
                <p class="text-white/80 text-sm mb-1">Perolehan Hari Ini</p>
                <h2 class="text-3xl font-bold">{{ new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(volunteerStore.stats.today.amount) }}</h2>
             </div>
             <div class="bg-white/20 p-2 rounded-lg">
                <TrendingUp class="w-6 h-6 text-white" />
             </div>
          </div>
          <div class="flex gap-4">
             <div class="bg-black/10 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
                <Wallet class="w-4 h-4 text-white/90" />
                <span>{{ volunteerStore.stats.today.count }} Transaksi</span>
             </div>
             <div class="bg-black/10 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
                <Trophy class="w-4 h-4 text-white/90" />
                <span>Peringkat #{{ volunteerStore.stats.rank }}</span>
             </div>
          </div>
       </div>
    </div>

    <!-- Quick Stats / Attendance Status -->
    <div 
      class="rounded-2xl p-6 text-white mb-6 shadow-xl relative overflow-hidden transition-all active:scale-95 group"
      :class="attendanceStore.isClockedIn ? 'bg-gradient-to-br from-[#8dab22] to-[#6d8810]' : 'bg-gradient-to-br from-primary to-[#163c48]'"
      @click="navigate('/attendance')"
    >
      <!-- Background Decoration -->
      <div class="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-colors"></div>
      <div class="absolute -left-6 -bottom-6 w-24 h-24 bg-black/10 rounded-full blur-2xl"></div>

      <div class="relative z-10 flex justify-between items-center">
        <div>
          <p class="text-white/80 text-xs font-medium uppercase tracking-wider mb-1">Status Absensi</p>
          <h2 class="text-2xl font-bold mb-2">{{ attendanceStore.isClockedIn ? 'Sedang Bekerja' : 'Belum Masuk' }}</h2>
          
          <div class="inline-flex items-center gap-1.5 text-sm font-medium opacity-80 hover:opacity-100 transition-opacity">
             <span>{{ attendanceStore.isClockedIn ? 'Ketuk absen pulang' : 'Ketuk absen masuk' }}</span>
             <ChevronRight class="w-4 h-4" />
          </div>
        </div>

        <!-- Large Icon Modern Accent -->
        <div class="relative flex items-center justify-center w-16 h-16">
            <!-- Soft Glow Background -->
            <div class="absolute inset-0 bg-white/30 rounded-full blur-xl opacity-50"></div>
            <Clock class="w-10 h-10 text-white relative z-10 drop-shadow-md" stroke-width="2" />
            
            <!-- Pulse Effect if not clocked in -->
            <div v-if="!attendanceStore.isClockedIn" class="absolute inset-0 bg-white/20 rounded-full animate-ping opacity-30"></div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <!-- Quick Actions -->
    <div class="grid grid-cols-4 gap-4 mb-6">
      <button 
        v-if="false"
        @click="navigate('/fundraising')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-2xl shadow-sm flex items-center justify-center text-orange-600 dark:text-orange-400 group-active:scale-95 transition-transform">
          <HandCoins class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Donasi</span>
      </button>

      <button 
        @click="navigate('/history')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-2xl shadow-sm flex items-center justify-center text-purple-600 dark:text-purple-400 group-active:scale-95 transition-transform">
          <Clock class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Riwayat</span>
      </button>

      <button 
        @click="navigate('/team-attendance')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-cyan-100 dark:bg-cyan-900/30 rounded-2xl shadow-sm flex items-center justify-center text-cyan-600 dark:text-cyan-400 group-active:scale-95 transition-transform">
          <Users class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Tim</span>
      </button>
      
      <button 
        @click="navigate('/requests')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-teal-100 dark:bg-teal-900/30 rounded-2xl shadow-sm flex items-center justify-center text-teal-600 dark:text-teal-400 group-active:scale-95 transition-transform">
          <FileText class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Pengajuan</span>
      </button>

      <button 
        @click="navigate('/expense-claims')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-pink-100 dark:bg-pink-900/30 rounded-2xl shadow-sm flex items-center justify-center text-pink-600 dark:text-pink-400 group-active:scale-95 transition-transform">
          <Receipt class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Klaim</span>
      </button>

      <button 
        @click="navigate('/visits')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-2xl shadow-sm flex items-center justify-center text-blue-600 dark:text-blue-400 group-active:scale-95 transition-transform">
          <MapPin class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Kunjungan</span>
      </button>

      <button 
        @click="navigate('/approvals')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl shadow-sm flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-active:scale-95 transition-transform">
          <FileCheck class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Approval</span>
      </button>

      <button 
        @click="navigate('/calendar')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl shadow-sm flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-active:scale-95 transition-transform">
          <Calendar class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Jadwal</span>
      </button>

      <button 
        @click="navigate('/sop')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 rounded-2xl shadow-sm flex items-center justify-center text-amber-600 dark:text-amber-400 group-active:scale-95 transition-transform">
          <BookOpen class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">SOP</span>
      </button>
      <button 
        @click="navigate('/payslips')"
        class="flex flex-col items-center justify-center space-y-2 group"
      >
        <div class="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-2xl shadow-sm flex items-center justify-center text-green-600 dark:text-green-400 group-active:scale-95 transition-transform">
          <Banknote class="w-7 h-7" />
        </div>
        <span class="text-[10px] font-medium text-gray-700 dark:text-slate-300 text-center leading-tight">Slip Bisyaroh</span>
      </button>
    </div>

    <!-- Announcements -->
    <div class="flex items-center justify-between mb-4">
        <h2 class="text-sm font-semibold text-gray-800 dark:text-white">Info Terbaru</h2>
        <button @click="navigate('/inbox')" class="text-xs font-bold text-primary dark:text-primary-400">Lihat Semua</button>
    </div>
    <div class="space-y-3">
        <div v-if="announcementStore.loading" class="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4 animate-pulse">
            <div class="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
            <div class="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
        </div>
        <div v-else-if="announcementStore.announcements.length === 0" class="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4 text-center">
             <p class="text-gray-500 dark:text-slate-400 text-sm">Tidak ada pengumuman baru.</p>
        </div>
        <div 
            v-else
            v-for="a in announcementStore.announcements.slice(0, 1)" 
            :key="a.id" 
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4 border-l-4"
            :class="{
                'border-blue-500': a.type === 'info',
                'border-yellow-500': a.type === 'warning',
                'border-red-500': a.type === 'important'
            }"
            @click="navigate('/inbox')"
        >
            <h3 class="font-bold text-gray-900 dark:text-white mb-1">{{ a.title }}</h3>
            <p class="text-gray-600 dark:text-slate-300 text-sm line-clamp-2">{{ a.content }}</p>
            <span class="text-xs text-gray-400 dark:text-slate-500 mt-2 block">{{ new Date(a.created_at).toLocaleDateString() }}</span>
        </div>
    </div>
  </div>
</template>
